import React from 'react';
import PropTypes from 'prop-types';
import { useFormContext, useFieldArray } from 'react-hook-form';
import { parse, isBefore } from 'date-fns';
import Isvg from 'react-inlinesvg';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';

import { ICONS } from 'constants/icons';
import { bem, classNameWithModifiers as withMods } from 'helpers/class-name';
import { AutocompleteTimePicker, Button } from 'components/HookForm';
import Message from 'components/Message';
import { dayShape } from 'constants/propTypes';

import { DEFAULT_START_TIME, DEFAULT_END_TIME } from '../../ManageServices.constants';
import { messages } from './RecurringSchedule.messages';
import './RecurringSchedule.scss';

function recurrenceValidator(getValues, startName, endName) {
  return {
    value: () => {
      const values = getValues();
      const startTime = _get(values, startName);
      const endTime = _get(values, endName);

      const parsedStartTime = parse(startTime, 'HH:mm', new Date());
      const parsedEndTime = parse(endTime, 'HH:mm', new Date());

      return isBefore(parsedStartTime, parsedEndTime);
    },
    message: messages.startTimeAfterEndTime,
  };
}

const { block, element } = bem('recurring-schedule');

function RecurringScheduleDow({ day, name }) {
  const { en: dayOfWeek, long: dayOfWeekLong } = day;
  const { control, getValues, watch } = useFormContext();
  const { fields, append, remove } = useFieldArray({ name: `${name}.${dayOfWeek}`, control });

  const values = watch();
  const isDisabled = _isEmpty(Object.keys(values)?.filter(key => key.includes(dayOfWeek)));

  return (
    <div key={dayOfWeek} className={withMods(element('day-row'), { isDisabled }, 'grid', 'grid--flush')}>
      <div className="grid__item 2/12 p">{dayOfWeekLong}</div>
      <div className="grid__item 8/12 pt-">
        {fields.map(({ id, startTime, endTime }, idx) => {
          const validator = recurrenceValidator(
            getValues,
            `${name}.${dayOfWeek}[${idx}].startTime`,
            `${name}.${dayOfWeek}[${idx}].endTime`
          );

          return (
            <div key={id} className="grid grid--flush">
              <AutocompleteTimePicker
                name={`${name}.${dayOfWeek}[${idx}].startTime`}
                defaultValue={startTime}
                className="grid__item 4/12"
                validate={validator}
              />
              <p className="grid__item 2/12 text--center pt-">-</p>
              <AutocompleteTimePicker
                name={`${name}.${dayOfWeek}[${idx}].endTime`}
                defaultValue={endTime}
                className="grid__item 4/12"
                validate={validator}
              />
              <Button className="grid__item 2/12" styled={false} onClick={() => remove(idx)}>
                <Isvg src={ICONS.trashCan} />
              </Button>
            </div>
          );
        })}
      </div>
      <button
        type="button"
        className={element('add-button', 'grid__item 2/12', 'p')}
        data-cypress="add-exception-day-button"
        onClick={() => append({ startTime: DEFAULT_START_TIME, endTime: DEFAULT_END_TIME })}>
        <Isvg src={ICONS.addBlue} />
      </button>
    </div>
  );
}

RecurringScheduleDow.propTypes = {
  day: dayShape,
  name: PropTypes.string,
};

function RecurringSchedule(props) {
  const { name = 'recurrence', daysOfWeek } = props;

  return (
    <div className={block()}>
      <Message as="label" className={element('label')} message={messages.label} />
      <Message as="p" className={element('description')} message={messages.description} />
      {daysOfWeek.map(day => (
        <RecurringScheduleDow key={day.en} day={day} name={name} />
      ))}
    </div>
  );
}

RecurringSchedule.propTypes = {
  name: PropTypes.string,
  daysOfWeek: PropTypes.arrayOf(dayShape),
};

export default RecurringSchedule;
